// pages/admin/UserSettings.js
import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Alert,
  Row,
  Col,
  Tab,
  Tabs,
  Badge,
} from "react-bootstrap";
import api from "../../services/api";
import Loader from "../../components/common/Loader";
import { Person, Key, Shield } from "react-bootstrap-icons";

const UserSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");

  // Form states
  const [profileForm, setProfileForm] = useState({
    username: "",
    email: "",
    fullName: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get("/auth/me");

      if (response.data.success) {
        setUser(response.data.admin);
        setProfileForm({
          username: response.data.admin.username,
          email: response.data.admin.email,
          fullName: response.data.admin.fullName || "",
        });
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      setError("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("adminToken");
      const response = await api.put("/admin/profile", profileForm);

      if (response.data.success) {
        setSuccess("Profile updated successfully");
        setUser(response.data.admin);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setError(error.response?.data?.error || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const response = await api.put("/admin/profile", {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      if (response.data.success) {
        setSuccess("Password changed successfully");
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setError(error.response?.data?.error || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "super_admin":
        return <Badge bg="danger">{role}</Badge>;
      case "counselor":
        return <Badge bg="primary">{role}</Badge>;
      default:
        return <Badge bg="secondary">{role}</Badge>;
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <Person className="me-2" />
          User Settings
        </h2>
        <div>
          {getRoleBadge(user?.role)}
          <span className="ms-2 text-muted">
            Logged in as: {user?.username}
          </span>
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess("")} dismissible>
          {success}
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
          >
            <Tab
              eventKey="profile"
              title={
                <div className="d-flex align-items-center">
                  <Person className="me-2" />
                  Profile
                </div>
              }
            >
              <div className="p-3">
                <h5 className="mb-4">Profile Information</h5>
                <Form>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          type="text"
                          value={profileForm.username}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              username: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={profileForm.email}
                          onChange={(e) =>
                            setProfileForm({
                              ...profileForm,
                              email: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={profileForm.fullName}
                      onChange={(e) =>
                        setProfileForm({
                          ...profileForm,
                          fullName: e.target.value,
                        })
                      }
                      placeholder="Enter your full name"
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      onClick={handleProfileUpdate}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </Form>
              </div>
            </Tab>

            <Tab
              eventKey="password"
              title={
                <div className="d-flex align-items-center">
                  <Key className="me-2" />
                  Password
                </div>
              }
            >
              <div className="p-3">
                <h5 className="mb-4">Change Password</h5>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          currentPassword: e.target.value,
                        })
                      }
                      placeholder="Enter current password"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>New Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          newPassword: e.target.value,
                        })
                      }
                      placeholder="Enter new password"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Confirm New Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          confirmPassword: e.target.value,
                        })
                      }
                      placeholder="Confirm new password"
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      onClick={handlePasswordChange}
                      disabled={saving}
                    >
                      {saving ? "Changing Password..." : "Change Password"}
                    </Button>
                  </div>
                </Form>
              </div>
            </Tab>

            <Tab
              eventKey="security"
              title={
                <div className="d-flex align-items-center">
                  <Shield className="me-2" />
                  Security
                </div>
              }
            >
              <div className="p-3">
                <h5 className="mb-4">Security Settings</h5>
                <div className="mb-4">
                  <h6>Account Information</h6>
                  <p className="text-muted">
                    <strong>Role:</strong> {user?.role}
                    <br />
                    <strong>Account Created:</strong>{" "}
                    {new Date(user?.createdAt).toLocaleDateString()}
                    <br />
                    <strong>Last Login:</strong>{" "}
                    {user?.lastLogin
                      ? new Date(user?.lastLogin).toLocaleString()
                      : "Never"}
                  </p>
                </div>

                <div className="mb-4">
                  <h6>Permissions</h6>
                  {user?.permissions && user.permissions.length > 0 ? (
                    <div className="d-flex flex-wrap gap-1">
                      {user.permissions.map((permission, index) => (
                        <Badge key={index} bg="info">
                          {permission}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">
                      No special permissions assigned
                    </p>
                  )}
                </div>

                <div className="alert alert-warning">
                  <small>
                    <strong>Note:</strong> For security reasons, role and
                    permissions can only be changed by a super administrator.
                  </small>
                </div>
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </div>
  );
};

export default UserSettings;
